import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Upload, X, ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export const getDisplayUrl = (asset: Pick<MediaAsset, "public_url" | "path" | "bucket">) => {
  if (asset.public_url) return asset.public_url;
  if (asset.path) {
    const { data } = supabase.storage.from(asset.bucket || "media").getPublicUrl(asset.path);
    return data.publicUrl;
  }
  return "";
};

export const MediaThumb = ({
  asset,
  className = "h-full w-full object-cover",
  fit = "cover",
}: {
  asset: MediaAsset;
  className?: string;
  fit?: "cover" | "contain";
}) => {
  const [failed, setFailed] = useState(false);
  const url = getDisplayUrl(asset);
  if (failed || !url) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted/40 p-2 text-center">
        <ImageIcon className="h-6 w-6 text-muted-foreground" aria-hidden />
        <p className="line-clamp-2 text-[10px] text-muted-foreground">
          {asset.file_name || "Image unavailable"}
        </p>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={asset.alt_text ?? asset.file_name ?? "Media asset"}
      className={`${className} ${fit === "contain" ? "object-contain" : "object-cover"}`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};


export interface MediaAsset {
  id: string;
  bucket: string;
  path: string;
  public_url: string;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  caption: string | null;
  usage_label: string | null;
  is_archived: boolean;
}

interface MediaPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (asset: MediaAsset) => void;
}

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 8 * 1024 * 1024; // 8MB

export const sanitizeFileName = (name: string) =>
  name
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "file";

export const buildStoragePath = (file: File) => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const ext = (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || "jpg").toLowerCase();
  const base = sanitizeFileName(file.name);
  const random = Math.random().toString(36).slice(2, 8);
  return `${yyyy}/${mm}/${base}-${random}.${ext}`;
};

const detectImageDims = (file: File): Promise<{ width?: number; height?: number }> =>
  new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const dims = { width: img.naturalWidth, height: img.naturalHeight };
        URL.revokeObjectURL(url);
        resolve(dims);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      img.src = url;
    } catch {
      resolve({});
    }
  });

export async function uploadMediaFile(file: File, userId: string | null): Promise<MediaAsset> {
  if (!ACCEPTED.includes(file.type)) {
    throw new Error("Unsupported file type. Use JPG, PNG, or WEBP.");
  }
  if (file.size > MAX_BYTES) {
    throw new Error("File is too large. Max 8MB.");
  }
  const path = buildStoragePath(file);
  const { error: upErr } = await supabase.storage
    .from("media")
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) throw upErr;
  const { data: pub } = supabase.storage.from("media").getPublicUrl(path);
  const dims = await detectImageDims(file);
  const { data, error } = await supabase
    .from("media_assets")
    .insert({
      bucket: "media",
      path,
      public_url: pub.publicUrl,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      width: dims.width ?? null,
      height: dims.height ?? null,
      created_by: userId,
      updated_by: userId,
    } as never)
    .select()
    .single();
  if (error) throw error;
  return data as MediaAsset;
}

const MediaPicker = ({ open, onOpenChange, onSelect }: MediaPickerProps) => {
  const { user } = useAdminAuth();
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open]);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("media_assets")
      .select("*")
      .eq("is_archived", false)
      .order("created_at", { ascending: false })
      .limit(200);
    if (err) setError(err.message);
    else setAssets((data ?? []) as MediaAsset[]);
    setLoading(false);
  };

  const filtered = assets.filter((a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.file_name?.toLowerCase().includes(q) ||
      a.alt_text?.toLowerCase().includes(q) ||
      a.caption?.toLowerCase().includes(q) ||
      a.usage_label?.toLowerCase().includes(q)
    );
  });

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const asset = await uploadMediaFile(f, user?.id ?? null);
      setAssets((prev) => [asset, ...prev]);
      toast.success("Uploaded");
      onSelect(asset);
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <DialogTitle className="font-headline text-2xl">Media Library</DialogTitle>
          <DialogDescription>
            Choose an existing image or upload a new one. JPG, PNG, or WEBP. Max 8MB.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search filename, alt text, caption, label"
              className="rounded-none pl-9"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED.join(",")}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
          <Button
            variant="gold"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="rounded-none"
          >
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Upload new
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="p-6 text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
            <ImageIcon className="h-8 w-8" />
            <p>No media yet. Upload your first image.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  onSelect(a);
                  onOpenChange(false);
                }}
                className="group relative aspect-square overflow-hidden border border-border bg-muted/30 transition hover:border-accent"
              >
                <MediaThumb asset={a} className="h-full w-full transition group-hover:scale-105" />
                {a.usage_label ? (
                  <span className="absolute left-1 top-1 bg-background/80 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.1em]">
                    {a.usage_label}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MediaPicker;
